import { FC, useMemo, useRef } from 'react';

import dashboardCampaignsFilterSearchFormFieldTouch from '@hh.ru/analytics-js-events/build/clickme/employer/common/dashboard_campaigns_filter_search_form_field_touch';
import { Breakpoint, Flex, SearchInput, useBreakpoint, useDebounce } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import { AllFiltersButton } from 'components/CampaignsDashboard/components/CampaignsFiltersPanel/components/AllFiltersButton';
import { CampaignSingleFilter } from 'components/CampaignsDashboard/components/CampaignsFiltersPanel/components/CampaignSingleFilter/CampaignSingleFilter';
import { SelectColumnsButton } from 'components/CampaignsDashboard/components/CampaignsFiltersPanel/components/SelectColumnsButton';
import { useApplyCampaignsFilters } from 'components/CampaignsDashboard/components/CampaignsFiltersPanel/useApplyCampaignsFilters';
import { needToShowFilter } from 'components/CampaignsDashboard/components/CampaignsFiltersPanel/utils';

import {
    setIsCampaignsDetailsLoading,
    setIsCampaignsLoading,
    setIsCampaignsStatisticsLoading,
} from 'store/models/campaignsDashboard';
import { CampaignFilter, setCampaignsFiltersSearchValue } from 'store/models/campaignsFilters';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    searchPlaceholder: 'CampaignsFiltersPanel.searchPlaceholder',
    searchPlaceholderShort: 'CampaignsFiltersPanel.searchPlaceholderShort',
};

const DISPLAYED_FILTERS_COUNT = {
    [Breakpoint.XS]: 0,
    [Breakpoint.S]: 0,
    [Breakpoint.M]: 2,
    [Breakpoint.L]: 3,
    [Breakpoint.XL]: 3,
    [Breakpoint.XXL]: 4,
};

export const CampaignsFiltersPanel: FC = () => {
    const campaignsSearchValue = useSelector((state) => state.campaignsFilters.campaignsSearchValue);
    const campaignsFilters = useSelector((state) => state.campaignsFilters.campaignsFilters);

    const trls = useTrls();
    const dispatch = useDispatch();
    const { isGtM, isMobile, breakpoint } = useBreakpoint();
    const { applyCampaignsFilters, abortRequest } = useApplyCampaignsFilters();

    const filters = useMemo(
        () =>
            (Object.entries(campaignsFilters) as [CampaignFilter, Record<string, string>][])
                .filter(([filterName, filterList]) => needToShowFilter(filterName, filterList))
                .map(([filterName, filterList]) => ({ filterName, filterList })),
        [campaignsFilters]
    );

    const handleSearchRef = useRef((newSearchValue: string) => {
        applyCampaignsFilters({ searchValue: newSearchValue });
    });

    const debouncedSearch = useDebounce(handleSearchRef.current, 500);

    const searchValueChangeHandler = (newSearchValue: string) => {
        dispatch(setCampaignsFiltersSearchValue(newSearchValue));

        const normalizedNewSearchValue = newSearchValue.trim();
        const normalizedCurrentSearchValue = campaignsSearchValue.trim();

        if (normalizedNewSearchValue === normalizedCurrentSearchValue) {
            return;
        }

        abortRequest();
        dispatch([
            setIsCampaignsLoading(true),
            setIsCampaignsDetailsLoading(true),
            setIsCampaignsStatisticsLoading(true),
        ]);
        debouncedSearch(newSearchValue);
    };

    return (
        <Flex columnGap={12}>
            <SearchInput
                data-qa="campaigns-search-input"
                value={campaignsSearchValue}
                onChange={searchValueChangeHandler}
                placeholder={trls(isGtM ? TrlKeys.searchPlaceholder : TrlKeys.searchPlaceholderShort)}
                clearable
                onFocus={() => {
                    dashboardCampaignsFilterSearchFormFieldTouch();
                }}
            />
            {filters.slice(0, DISPLAYED_FILTERS_COUNT[breakpoint]).map(({ filterName, filterList }) => (
                <div key={filterName}>
                    <CampaignSingleFilter filterName={filterName} filterList={filterList} />
                </div>
            ))}
            {filters.length > DISPLAYED_FILTERS_COUNT[breakpoint] && <AllFiltersButton />}
            {!isMobile && <SelectColumnsButton />}
        </Flex>
    );
};
